<template>
    <div class="p-xs rounded-sm border border-(--border-subtle)">
        <DropdownContent default-open>
            <template #trigger>
                <div class="flex w-full items-center justify-between">
                    <p class="text-md font-default font-normal">{{ name }}</p>
                    <TagComponent
                        :value="['100', '0', '0-0'].includes(formattedData) ? { '100': 'Best effort', '0': 'Not applicable', '0-0': 'Not applicable' }[formattedData]! : formatData() + appendUnit(unit)"
                        :variant="['0-0', '0'].includes(formattedData) ? 'silver' : 'default'"
                        class-name="font-default text-sm font-bold text-(--state-info-fg)"
                    />
                </div>
            </template>

            <template #content>
                <div class="gap-sm pt-sm pl-sm flex flex-col">
                    <ul class="gap-xs flex justify-between">
                        <li v-for="(option, i) in options" :key="option.value" class="flex-1">
                            <button
                                class="btn-option font-default w-full bg-(--surface-highlight) font-normal text-primary"
                                :disabled="disabled"
                                :class="{ chosen: chosenOption === i }"
                                @click="(e) => choose(e, option.value, i)"
                            >
                                {{ option.label }}
                            </button>
                        </li>
                        <li class="flex-1">
                            <button
                                class="btn-option w-full"
                                :class="{ chosen: chosenOption === options.length }"
                                :disabled="disabled"
                                @click="(e) => choose(e)"
                            >
                                Custom
                            </button>
                        </li>
                    </ul>

                    <div v-if="unit === '%' || unit === 'C'" class="gap-sm flex flex-col">
                        <DoubleSlider
                            v-if="!disabled"
                            :disabled="disabled"
                            :min="range.min"
                            :max="range.max"
                            :override-min="Number(chosenValue[0])"
                            :override-max="Number(chosenValue[1])"
                            @override="onSliderOverride"
                        />
                        <div class="gap-md font-default text-md flex justify-between font-normal">
                            <div class="gap-xs flex w-1/4 flex-col">
                                <label for="input-from">From</label>
                                <div class="input-wrapper">
                                    <input
                                        id="input-from"
                                        v-model="chosenValue[0]"
                                        :disabled="disabled"
                                        type="number"
                                        :min="range.min"
                                        :max="range.max"
                                        @keypress="isNumber($event)"
                                        @blur="clampFromValue"
                                    />
                                </div>
                            </div>
                            <div class="gap-xs flex flex-col">
                                <label for="input-to">To</label>
                                <div class="input-wrapper">
                                    <input
                                        id="input-to"
                                        v-model="chosenValue[1]"
                                        :disabled="disabled"
                                        type="number"
                                        :min="range.min"
                                        :max="range.max"
                                        @keypress="isNumber($event)"
                                        @blur="clampToValue"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-else-if="chosenOption === options.length"
                        class="font-default text-md gap-sm mt-sm flex flex-col font-normal"
                    >
                        <label for="input-hours">Custom</label>
                        <div class="input-wrapper">
                            <input
                                id="input-hours"
                                v-model="chosenValue[0]"
                                :disabled="disabled"
                                type="number"
                                :min="range.min"
                                :max="range.max"
                                @keypress="isNumber($event)"
                                @change="formatData"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </DropdownContent>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch, type PropType } from 'vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import DropdownContent from '../misc/dropdown-content.vue';
import DoubleSlider from './double-slider.vue';
import { isNumber } from '@/stores/helpers';
import { appendUnit } from '@/stores/helpers';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    unit: {
        type: String as PropType<'C' | 'Hours' | '%' | 'Days'>,
        required: true,
    },
    value: {
        type: [String],
        required: true,
    },
    disabled: Boolean,
});

// TODO: really needs a better name...
const optionOptions = {
    '%': [
        { label: '20-70%', value: '20-70' },
        { label: '20-80%', value: '20-80' },
        { label: '30-70%', value: '30-70' },
        { label: '30-80%', value: '30-80' },
    ],
    C: [
        { label: '18-29°C', value: '18-29' },
        { label: '19-29°C', value: '19-29' },
        { label: '18-30°C', value: '18-30' },
        { label: '19-30°C', value: '19-30' },
    ],
    Hours: [
        { label: 'Best effort', value: '100' },
        { label: '2h', value: '2' },
        { label: '4h', value: '4' },
        { label: '8h', value: '8' },
    ],
    Days: [
        { label: 'Best effort', value: '100' },
        { label: '14 days', value: '14' },
        { label: '30 days', value: '30' },
        { label: '60 days', value: '60' },
    ],
};

const rangeOptions = {
    '%': { min: 20, max: 80 },
    C: { min: 15, max: 32 },
    Hours: { min: 0, max: 24 },
    Days: { min: 0, max: 90 },
};

const options = optionOptions[props.unit];
const range = rangeOptions[props.unit];
const chosenOption = ref<number | undefined>(0);
const chosenValue = ref<number[]>(options[0].value.split('-').map(Number));
const formattedData = ref<string>('0');

function onSliderOverride([min, max]: number[]) {
    chosenValue.value[0] = min;
    chosenValue.value[1] = max;
    if (chosenOption.value === undefined && min && max) {
        chosenOption.value = options.length;
    }
}

function clampFromValue() {
    chosenValue.value[0] = Math.max(Math.min(chosenValue.value[0], range.max), range.min);
}

function clampToValue() {
    chosenValue.value[1] = Math.min(Math.max(chosenValue.value[1], range.min), range.max);
}

const choose = (event: Event, value?: string, index?: number) => {
    event.preventDefault();

    if ((!value && chosenOption.value === options.length) || (value && chosenOption.value === index)) {
        chosenValue.value = ['Hours', 'Days'].includes(props.unit) ? [0] : [0, 0];
        chosenOption.value = undefined;
        return;
    }

    if (!value) {
        // for hours we need to be able to toggle between actual values and 'Not applicable'
        chosenOption.value = options.length;
        return;
    } else {
        // index will always be defined if value is defined
        chosenOption.value = index!;
    }

    chosenValue.value = value.split('-').map(Number);
};

const emit = defineEmits(['change']);

watch(
    chosenValue,
    () => {
        if (
            chosenOption.value !== options.length &&
            chosenOption.value !== undefined &&
            chosenOption.value !== options.length + 1 &&
            chosenValue.value.join('-') !== options?.[chosenOption.value]?.value
        ) {
            chosenOption.value = options.length;
        }
        formattedData.value = formatData();
        emit('change', formattedData.value);
    },
    { deep: true },
);

const formatData = () => {
    if (chosenValue.value.length > 1 && chosenValue.value[0] !== 0) {
        return chosenValue.value.join('-');
    } else {
        return chosenValue.value[0].toString();
    }
};

onMounted(() => {
    // since hours have two values out of the scope
    // 'Not applicable' = 0 and 'Best effort' = 100
    // we set 'Not applicable' as the default value
    chosenValue.value = props.value.split('-').map(Number);
    if (props.unit === 'Hours' && props.value === '0') {
        chosenOption.value = undefined;
    } else {
        chosenOption.value = options.findIndex((option) => option.value === props.value);
    }
});
</script>
